
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle, XCircle, Play } from "lucide-react";
import { useProfileMentions } from "@/hooks/useProfileMentions";
import MentionApprovalModal from "@/components/press/MentionApprovalModal";
import VideoMentionModal from "@/components/press/VideoMentionModal";
import { useNavigate } from "react-router-dom";
import { useMembershipAccess } from "@/hooks/useMembershipAccess";

const PendingMentionsAlert: React.FC = () => {
  const { mentions, pendingMentions, updateMentionStatus } = useProfileMentions();
  const [selectedMention, setSelectedMention] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const navigate = useNavigate();
  const { hasAccessTo } = useMembershipAccess();

  // Separate pending article mentions (need approval) from video mentions (just notifications)
  const pendingArticleMentions = pendingMentions.filter(m => m.source_type === 'press_article');
  const recentVideoMentions = mentions.filter(m => m.source_type === 'video').slice(0, 2);

  // Show alert if there are pending article mentions or recent video mentions
  if (pendingArticleMentions.length === 0 && recentVideoMentions.length === 0) {
    return null;
  }

  const handleViewMention = (mention: any) => {
    if (mention.source_type === 'video') {
      setSelectedMention(mention);
      setVideoModalOpen(true);
    } else {
      setSelectedMention(mention);
      setModalOpen(true);
    }
  };

  const handleWatchVideo = () => {
    // Check if user has video membership access
    if (hasAccessTo('videos') || hasAccessTo('premium')) {
      // Navigate to video page
      navigate('/videos/hoops_recaps');
    } else {
      // Navigate to purchase/upgrade page
      navigate('/videos/hoops_recaps');
    }
  };

  const handleApprove = async (mentionId: string) => {
    await updateMentionStatus(mentionId, 'approved');
    setModalOpen(false);
  };

  const handleReject = async (mentionId: string) => {
    await updateMentionStatus(mentionId, 'rejected');
    setModalOpen(false);
  };

  return (
    <>
      {/* Article Mentions Alert (Require Approval) */}
      {pendingArticleMentions.length > 0 && (
        <Card className="border-blue-200 bg-blue-50 mb-4">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-blue-900 mb-1">
                  You have {pendingArticleMentions.length} pending article mention{pendingArticleMentions.length > 1 ? 's' : ''}
                </h3>
                <p className="text-sm text-blue-700 mb-3">
                  You've been mentioned in {pendingArticleMentions.length === 1 ? 'an article' : 'articles'} that require{pendingArticleMentions.length > 1 ? '' : 's'} your approval.
                </p>
                
                <div className="space-y-2">
                  {pendingArticleMentions.slice(0, 3).map((mention) => (
                    <div key={mention.id} className="flex items-center justify-between bg-white p-2 rounded border">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {mention.article_title}
                        </p>
                        <p className="text-xs text-gray-600">
                          by {mention.author_name}
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => handleViewMention(mention)}
                        className="ml-2"
                      >
                        Review
                      </Button>
                    </div>
                  ))}
                  
                  {pendingArticleMentions.length > 3 && (
                    <p className="text-sm text-blue-600 font-medium">
                      +{pendingArticleMentions.length - 3} more mention{pendingArticleMentions.length - 3 > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Video Mentions Alert (Just Notifications) */}
      {recentVideoMentions.length > 0 && (
        <Card className="border-green-200 bg-green-50 mb-4">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Play className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-green-900 mb-1">
                  You've been featured in {recentVideoMentions.length} video{recentVideoMentions.length > 1 ? 's' : ''}!
                </h3>
                <p className="text-sm text-green-700 mb-3">
                  Great news! You've been mentioned in recent videos. Click to watch now.
                </p>
                
                <div className="space-y-2">
                  {recentVideoMentions.map((mention) => (
                    <div key={mention.id} className="flex items-center justify-between bg-white p-2 rounded border">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {mention.article_title}
                        </p>
                        <p className="text-xs text-gray-600">
                          by {mention.author_name}
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={handleWatchVideo}
                        className="ml-2 bg-green-600 hover:bg-green-700"
                      >
                        <Play className="h-3 w-3 mr-1" />
                        Watch
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <MentionApprovalModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mention={selectedMention}
        article={selectedMention ? {
          id: selectedMention.source_id,
          title: selectedMention.article_title,
          content: '',
          author_name: selectedMention.author_name
        } : null}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      <VideoMentionModal
        isOpen={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        mention={selectedMention}
        video={selectedMention ? {
          id: selectedMention.source_id,
          title: selectedMention.article_title,
          description: '',
          author_name: selectedMention.author_name
        } : null}
      />
    </>
  );
};

export default PendingMentionsAlert;
